import { Link, router, usePoll } from '@inertiajs/react';
import { ArrowRight, Trophy } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { JudgeBallot } from '@/types';
import { flushAll, useAutosaveStatus } from './autosave';
import { BallotHeader, Notice } from './BallotHeader';
import { ProjectRow } from './ProjectRow';
import { ProjectSheet } from './ProjectSheet';

// How often the running order is re-read, so the project on stage moves without a
// reload. Only the `ballot` prop travels; what a judge is typing is local state.
const POLL_MS = 5000;

// The stage page: every project in running order, graded as it presents. Once all of
// them are graded the judge moves on to the final check (Judges/Stages/Final).
export function Ballot({
  ballot,
  judgeName,
  eventName,
}: {
  ballot: JudgeBallot;
  judgeName: string;
  eventName: string;
}) {
  const completed = ballot.completedAt !== null;
  usePoll(POLL_MS, { only: ['ballot'] }, { autoStart: !completed });
  const [detailId, setDetailId] = useState<string | null>(null);

  const current = ballot.projects.find((p) => p.status === 'current');
  const detail = ballot.projects.find((p) => p.id === detailId) ?? null;
  const graded = ballot.projects.filter(
    (p) =>
      ballot.criteria.length > 0 &&
      ballot.criteria.every((c) => p.scores[c.id] !== undefined),
  ).length;
  const finalPath = `/judges/stages/${ballot.stageSlug}/final`;
  const autosave = useAutosaveStatus();
  const [leaving, setLeaving] = useState(false);
  // A grade still waiting for its debounce may be the last one missing, so the button
  // also opens while something is pending; the server has the final word.
  const canLeave = ballot.completionBlocker === null || autosave === 'saving';

  const goToFinalCheck = async () => {
    setLeaving(true);
    await flushAll();
    router.visit(finalPath, { onFinish: () => setLeaving(false) });
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8 sm:py-12">
      <BallotHeader
        ballot={ballot}
        judgeName={judgeName}
        eventName={eventName}
        back={{ href: '/judges', label: 'Mis stages' }}
        summary={`Notas de 0 a 5 · ${graded}/${ballot.projects.length} evaluados`}
      />

      {completed && (
        <Button asChild variant="outline" className="w-fit">
          <Link href={finalPath}>
            <Trophy className="mr-2 h-4 w-4" />
            Ver mi resultado final
          </Link>
        </Button>
      )}

      {ballot.projects.length === 0 ? (
        <Notice>Este stage todavía no tiene proyectos.</Notice>
      ) : (
        <div className="flex flex-col gap-4">
          {ballot.projects.map((project) => (
            <ProjectRow
              key={project.id}
              stageSlug={ballot.stageSlug}
              project={project}
              criteria={ballot.criteria}
              scrollIntoView={!completed && project.id === current?.id}
              readOnly={completed}
              onExpand={() => setDetailId(project.id)}
            />
          ))}
        </div>
      )}

      {!completed && ballot.projects.length > 0 && (
        <div className="flex flex-col gap-3 border-2 border-primary border-dashed p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <p className="font-bold text-primary">Final check</p>
            <p className="text-primary/70">
              {ballot.completionBlocker ??
                'Ya evaluaste todos los proyectos. Revisa el ranking y finaliza tu evaluación.'}
            </p>
          </div>
          <Button
            className="shrink-0"
            disabled={!canLeave || leaving}
            onClick={goToFinalCheck}
          >
            {leaving ? 'Guardando…' : 'Ir al final check'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      <ProjectSheet project={detail} onClose={() => setDetailId(null)} />
    </div>
  );
}
